import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';
import { Autocomplete, TextField, Tooltip } from '@mui/material';
import AddAccountCardModal from '../Widgets/AddAccountCardModal/AddAccountCardModal.jsx';
import XtramModal, {
    ComboPopper,
    XtramButton,
    XtramField,
    XtramInput,
    XtramSelect,
    xtramStyles,
} from '../Widgets/XtramModal/XtramModal';
import Constants from '../../assests/Constants/constants.js';
import fieldStyles from '../JournalEntryies/SubComponents/AddJournalEntryModal.module.scss';

const AddOrEditBankDialog = ({
    open,
    onClose,
    isEditingBank,
    selectedBankForCreate,
    bankOptions,
    handleChangeBankForCreate,
    bankBranch,
    handleChangeCreateBank,
    bankAccountNumber,
    cardForNewBank,
    foreignCurrencyCode,
    processedCardsData,
    setCardForNewBank,
    handleAddOrEditBank,
    handleRemoveBank,
    client,
    token,
    setLoading,
    palCodes,
    fetchEnums,
}) => {
    const { t, i18n } = useTranslation();
    const direction = i18n.dir();
    const fieldTextAlign = direction === 'ltr' ? 'left' : 'right';

    const prevOpenRef = useRef(false);
    const editingInitialForeignCurrencyRef = useRef(null);

    useEffect(() => {
        if (open && !prevOpenRef.current && isEditingBank) {
            editingInitialForeignCurrencyRef.current = foreignCurrencyCode;
        }
        prevOpenRef.current = open;
    }, [open, isEditingBank, foreignCurrencyCode]);

    const [creditDebitSearchTerm, setCreditDebitSearchTerm] = useState('');
    const [newAccountCard, setNewAccountCard] = useState({ accountCodeName: '', accountCodeId: '' });
    const [isEditAccountCard, setIsEditAccountCard] = useState(false);
    const [isAddAccountCard, setIsAddAccountCard] = useState(false);
    const [palCode, setPalCode] = useState('בנקים');
    const [newCardId, setNewCardId] = useState(null);
    const [addAccountCardType, setAddAccountCardType] = useState('cardForNewBank');
    const [cardIdOption, setCardIdOption] = useState(-1);

    const isFormValid = useMemo(() => {
        return Boolean(
            selectedBankForCreate &&
            bankBranch.trim() &&
            bankAccountNumber.trim() &&
            cardForNewBank
        );
    }, [selectedBankForCreate, bankBranch, bankAccountNumber, cardForNewBank]);

    const currencyOptions = useMemo(() => (
        Object.entries(Constants.ForeignCurrencyCode).map(([code, num]) => ({
            label: code,
            value: num,
        }))
    ), []);

    const formattedAccountCardOptions = processedCardsData.map((option) => ({
        value: option.cardId,
        label: option.accountCaseId
            ? `${option.name} - ${option?.accountCaseId || ''}`
            : `${option.name}`,
        name: option.name,
        accountCaseId: option.accountCaseId || '',
        cardId: option.cardId,
        value1: option.value1,
        palCode: option.palCode,
        isEditable: option.isEditable
    }));

    useEffect(() => {
        if (processedCardsData && processedCardsData.length > 0) {
            const maxCardId = Math.max(
                ...processedCardsData.slice(1).map((option) => parseInt(option.cardId, 10))
            );

            setCardIdOption(maxCardId + 1);
        }
    }, [processedCardsData]);

    const handleChangeAfterAdd = (cardId, accountType) => {
        const newCard = {
            cardId,
            type: accountType,
        };
        setNewCardId(newCard);
    };

    useEffect(() => {
        if (processedCardsData && processedCardsData.length > 0 && newCardId) {
            if (newCardId.type === 'cardForNewBank') {
                setCardForNewBank(newCardId.cardId);
            }
            setNewCardId(null);
        }
    }, [processedCardsData, newCardId, setCardForNewBank]);

    const EditAccountCard = (selectedItem, accountType) => {
        if (!selectedItem) {
            console.warn('No account card selected for editing.');
            return;
        }
        setPalCode(selectedItem.palCode);
        setNewAccountCard({
            accountCodeName: selectedItem.name || '',
            accountCodeId: selectedItem.cardId,
            accountCaseId: selectedItem.accountCaseId || '',
        });
        setIsEditAccountCard(true);
        setAddAccountCardType(accountType);
        setIsAddAccountCard(true);
    };

    const openAddAccountCard = (searchTerm = '') => {
        setIsEditAccountCard(false);
        setAddAccountCardType('cardForNewBank');
        setNewAccountCard({
            accountCodeName: searchTerm || '',
            accountCodeId: cardIdOption,
        });
        setIsAddAccountCard(true);
    };

    const handleCreditDebitForJournalEntry = (newValue) => {
        if (!newValue) {
            setCardForNewBank(null);
            return;
        }

        if (newValue.value1 === 'AddItem') {
            openAddAccountCard(newValue.searchTerm || '');
            return;
        }

        setCardForNewBank(newValue.cardId);
    };

    const handleNoOptionCreditDebitClick = (searchTerm) => {
        openAddAccountCard(searchTerm);
    };

    const handleCurrencyChange = (_, newValue) => {
        const nextVal = newValue ? newValue.value : null;
        const hadForeignWhenOpened = editingInitialForeignCurrencyRef.current != null;
        const removingForeign = nextVal == null && foreignCurrencyCode != null;

        if (isEditingBank && hadForeignWhenOpened && removingForeign) {
            (async () => {
                const result = await Swal.fire({
                    title: t('AreYouSure'),
                    text: t('RemoveForeignCurrencyWillConvertAmounts'),
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonColor: '#3085d6',
                    cancelButtonColor: '#d33',
                    confirmButtonText: t('Yes'),
                    cancelButtonText: t('No'),
                    customClass: { container: 'swal-high-zindex' },
                });
                if (result.isConfirmed) {
                    handleChangeCreateBank('foreignCurrencyCode', null);
                }
            })();
            return;
        }

        handleChangeCreateBank('foreignCurrencyCode', nextVal);
    };

    const renderComboPopper = useCallback((props) => (
        <ComboPopper {...props} direction={direction} />
    ), [direction]);

    const selectedCurrency = currencyOptions.find((option) => option.value === foreignCurrencyCode) || null;
    const selectedCard = formattedAccountCardOptions.find(
        (option) => option.value1 !== 'AddItem' && option.value === cardForNewBank
    ) || null;

    return (
        <>
            <XtramModal
                open={open}
                onClose={onClose}
                title={isEditingBank ? t('EditBankAccount') : t('SetBank')}
                direction={direction}
                footer={
                    <>
                        {isEditingBank && (
                            <XtramButton variant="danger" onClick={() => handleRemoveBank()}>{t('Delete')}</XtramButton>
                        )}
                        <XtramButton variant="secondary" onClick={onClose}>{t('Cancel')}</XtramButton>
                        <XtramButton onClick={() => handleAddOrEditBank()} disabled={!isFormValid}>
                            {isEditingBank ? t('Save') : t('SetBank')}
                        </XtramButton>
                    </>
                }
            >
                <div className={xtramStyles.formFields}>
                    <XtramField label={t('SelectTemplate')} required size="full">
                        <XtramSelect
                            direction={direction}
                            options={bankOptions}
                            value={selectedBankForCreate || null}
                            placeholder={t('SelectTemplate')}
                            onChange={handleChangeBankForCreate}
                        />
                    </XtramField>
                    <XtramField label={t('BankBranch')} required>
                        <XtramInput
                            direction={direction}
                            placeholder={t('BankBranch')}
                            value={bankBranch}
                            onChange={(e) => handleChangeCreateBank('bankBranch', e.target.value)}
                        />
                    </XtramField>
                    <XtramField label={t('BankAccountNumber')} required>
                        <XtramInput
                            direction={direction}
                            placeholder={t('BankAccountNumber')}
                            value={bankAccountNumber}
                            onChange={(e) => handleChangeCreateBank('bankAccountNumber', e.target.value)}
                        />
                    </XtramField>
                    <XtramField label={t('Currency')}>
                        <XtramSelect
                            direction={direction}
                            options={currencyOptions}
                            value={selectedCurrency}
                            placeholder={t('Currency')}
                            onChange={(option) => handleCurrencyChange(null, option)}
                        />
                    </XtramField>
                    <XtramField label={t('AccountCard')} required>
                        <Autocomplete
                            className={fieldStyles.combo}
                            size="small"
                            options={formattedAccountCardOptions}
                            getOptionLabel={(option) =>
                                option.value1 === 'AddItem'
                                    ? (option.name || '')
                                    : `${option.cardId} - ${option.name}`
                            }
                            value={selectedCard}
                            isOptionEqualToValue={(option, value) =>
                                option.value1 !== 'AddItem' && String(option.value) === String(value?.value)
                            }
                            onChange={(event, newValue) => handleCreditDebitForJournalEntry(newValue)}
                            onInputChange={(event, inputValue, reason) => {
                                if (reason === 'reset') return;
                                setCreditDebitSearchTerm(inputValue);
                            }}
                            PopperComponent={renderComboPopper}
                            noOptionsText={
                                <button
                                    type="button"
                                    className={fieldStyles.addOption}
                                    onMouseDown={(event) => {
                                        event.preventDefault();
                                        handleNoOptionCreditDebitClick(creditDebitSearchTerm);
                                    }}
                                >
                                    <span className={fieldStyles.addOptionPlus} aria-hidden="true">+</span>
                                    <span className={fieldStyles.addOptionLabel}>
                                        {t('AddAccountCard')}{creditDebitSearchTerm ? ` "${creditDebitSearchTerm}"` : ''}
                                    </span>
                                </button>
                            }
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder={t('AccountCard')}
                                    variant="standard"
                                    className={fieldStyles.styleInput}
                                    InputProps={{ ...params.InputProps, disableUnderline: true }}
                                    inputProps={{ ...params.inputProps, style: { textAlign: fieldTextAlign } }}
                                />
                            )}
                            renderOption={(props, option) => {
                                const { key, ...optionProps } = props;
                                if (option.value1 === 'AddItem') {
                                    return (
                                        <li
                                            key={key}
                                            {...optionProps}
                                            className={`${optionProps.className || ''} ${fieldStyles.addOption}`}
                                            onMouseDown={(event) => {
                                                event.preventDefault();
                                                event.stopPropagation();
                                                openAddAccountCard(creditDebitSearchTerm);
                                            }}
                                        >
                                            <span className={fieldStyles.addOptionPlus} aria-hidden="true">+</span>
                                            <span className={fieldStyles.addOptionLabel}>{option.name}</span>
                                        </li>
                                    );
                                }
                                return (
                                    <Tooltip
                                        key={key}
                                        title={option.isEditable ? t('RightClickToEdit') : t('NotEditable')}
                                        placement="top"
                                    >
                                        <li
                                            {...optionProps}
                                            onContextMenu={(event) => {
                                                event.preventDefault();
                                                if (option.isEditable) EditAccountCard(option, 'cardForNewBank');
                                            }}
                                        >
                                            {`${option?.cardId || ''} - ${option.name}`}
                                        </li>
                                    </Tooltip>
                                );
                            }}
                        />
                    </XtramField>
                </div>
            </XtramModal>
            <AddAccountCardModal
                accountCardOptions={processedCardsData}
                open={isAddAccountCard}
                onClose={() => setIsAddAccountCard(false)}
                editedItem={newAccountCard}
                isEdit={isEditAccountCard}
                direction={direction}
                client={client}
                token={token}
                setLoading={setLoading}
                handleChangeAfterAdd={handleChangeAfterAdd}
                fetchEnums={fetchEnums}
                type={addAccountCardType}
                palCodes={palCodes}
                propPalCode={palCode}
                setPalCode={setPalCode}
            />
        </>
    );
};

export default AddOrEditBankDialog;
